import { baseApiClient } from './baseService';

/**
 * AI Hold & Transfer status progression.
 */
export enum AIHoldAndTransferStatus {
  PENDING = 'pending',
  TRANSFER_INITIATED = 'transfer_initiated',
  TRANSFER_SUCCESSFUL = 'transfer_successful',
  TRANSFER_FAILED = 'transfer_failed',
  CALL_ENDED = 'call_ended',
}

/**
 * Request to start an AI hold-and-transfer call.
 */
export interface StartAIHoldAndTransferRequest {
  task_id: string;
  phone_number: string;
  transfer_to?: string | null;
  coverage_id?: string | null;
  matter_id?: string | null;
}

/**
 * Response after starting an AI hold-and-transfer workflow.
 */
export interface StartAIHoldAndTransferResponse {
  workflow_id: string;
  call_id?: string | null;
  message: string;
}

/**
 * Current status of an AI hold-and-transfer workflow.
 */
export interface AIHoldAndTransferStatusResponse {
  workflow_id: string;
  status: string;
  transfer_status: AIHoldAndTransferStatus;
  call_id?: string | null;
}

/**
 * Single item in list response.
 */
export interface AIHoldAndTransferListItem {
  workflow_id: string;
  coverage_id?: string | null;
  matter_id?: string | null;
  status: string;
  transfer_status: AIHoldAndTransferStatus;
  created_at?: string | null;
}

/**
 * List of active/recent workflows.
 */
export interface AIHoldAndTransferListResponse {
  workflows: AIHoldAndTransferListItem[];
}

/**
 * User's transfer phone info.
 */
export interface UserTransferPhoneResponse {
  has_phone: boolean;
  phone_number: string | null;
}

/**
 * AI Hold & Transfer API service.
 *
 * This service manages AI-powered hold-and-transfer calls where the AI:
 * 1. Calls the carrier phone number
 * 2. Navigates IVR menus and waits on hold
 * 3. Transfers the call to the user when a human answers
 */
const aiHoldAndTransferApi = baseApiClient
  .enhanceEndpoints({
    addTagTypes: ['AIHoldAndTransfer'] as const,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      /**
       * List active AI hold-and-transfer workflows for a matter.
       * Returns only workflows that are currently running (not completed/failed).
       */
      listAiHoldAndTransfer: builder.query<
        AIHoldAndTransferListResponse,
        { matterId: string }
      >({
        query: ({ matterId }) =>
          `api/ai-hold-and-transfer/?matter_id=${matterId}`,
        providesTags: (result, error, { matterId }) => [
          { type: 'AIHoldAndTransfer', id: matterId },
          'AIHoldAndTransfer',
        ],
      }),

      /**
       * Start an AI hold-and-transfer call.
       * The AI will call the specified phone number, wait on hold, and transfer
       * to the user when a human answers.
       */
      startAiHoldAndTransfer: builder.mutation<
        StartAIHoldAndTransferResponse,
        { startAiHoldAndTransferRequest: StartAIHoldAndTransferRequest }
      >({
        query: ({ startAiHoldAndTransferRequest }) => ({
          url: 'api/ai-hold-and-transfer/start/',
          method: 'POST',
          body: startAiHoldAndTransferRequest,
        }),
        invalidatesTags: (result, error, arg) => [
          {
            type: 'AIHoldAndTransfer',
            id: arg.startAiHoldAndTransferRequest.matter_id || 'LIST',
          },
          'AIHoldAndTransfer',
        ],
      }),

      /**
       * Get status of an AI hold-and-transfer workflow.
       */
      getAiHoldAndTransferStatus: builder.query<
        AIHoldAndTransferStatusResponse,
        { workflowId: string }
      >({
        query: ({ workflowId }) =>
          `api/ai-hold-and-transfer/${workflowId}/status/`,
      }),

      /**
       * Cancel a running AI hold-and-transfer workflow.
       */
      cancelAiHoldAndTransfer: builder.mutation<
        { workflow_id: string; message: string },
        { workflowId: string }
      >({
        query: ({ workflowId }) => ({
          url: `api/ai-hold-and-transfer/${workflowId}/cancel/`,
          method: 'POST',
        }),
        invalidatesTags: () => ['AIHoldAndTransfer'],
      }),

      /**
       * Get the current user's Dialpad phone number for transfer destination.
       */
      getUserTransferPhone: builder.query<UserTransferPhoneResponse, void>({
        query: () => 'api/ai-hold-and-transfer/user-transfer-phone/',
      }),
    }),
    overrideExisting: false,
  });

// Export hooks for usage in components
export const {
  useListAiHoldAndTransferQuery,
  useStartAiHoldAndTransferMutation,
  useGetAiHoldAndTransferStatusQuery,
  useCancelAiHoldAndTransferMutation,
  useGetUserTransferPhoneQuery,
} = aiHoldAndTransferApi;

export { aiHoldAndTransferApi };
